import { OPERATION_MODES, STORAGE_KEYS, createAuthSession, createCurrentUser, createDefaultCompany, createSyncConnection, createWorkspace, createWorkspaceUser } from './flowdeskDataModel.js';
import { normalizeSyncQueue } from './syncQueue.js';

const defaultPrayerStatus = () => ({
  Fajr: false,
  Dhuhr: false,
  Asr: false,
  Maghrib: false,
  Isha: false
});

export const normalizeCompanies = ({ companies, tasks, lang, colorPalettes }) => {
  if (!companies || typeof companies !== 'object') return {};
  const loadedCompanies = { ...companies };

  ['company2', 'company3', 'freelance'].forEach((legacyId) => {
    const company = loadedCompanies?.[legacyId];
    const isReferenced = Array.isArray(tasks) && tasks.some(task => task.category === legacyId);
    const hasUserData = Boolean(
      company?.logo ||
      company?.managers?.length ||
      company?.clients?.length ||
      company?.attendanceLogs?.length ||
      company?.overtimeMinutes ||
      company?.hourlyRate
    );

    if (company && !isReferenced && !hasUserData) {
      delete loadedCompanies[legacyId];
    }
  });

  if (!Object.keys(loadedCompanies).length) return {};

  Object.keys(loadedCompanies).forEach((key) => {
    const company = loadedCompanies[key];

    if (company?.theme?.id) {
      const foundTheme = colorPalettes.find(palette => palette.id === company.theme.id);
      company.theme = foundTheme || colorPalettes[0];
    } else {
      company.theme = colorPalettes[0];
    }

    const themeBg = company?.theme?.bg || '';
    if (themeBg.includes('purple') || themeBg.includes('fuchsia') || themeBg.includes('pink') || themeBg.includes('red') || themeBg.includes('blue-400')) {
      company.theme = colorPalettes[2] || colorPalettes[0];
    }

    if (themeBg.includes('blue') || themeBg.includes('cyan') || themeBg.includes('emerald') || themeBg.includes('sky') || themeBg.includes('indigo') || themeBg.includes('lime') || themeBg.includes('amber') || themeBg.includes('orange')) {
      company.theme = colorPalettes[0];
    }

    if (!company.workDays) company.workDays = [0, 1, 2, 3, 4, 5, 6];

    if (company.systemUrl === undefined) company.systemUrl = '';
    if (company.systemEnabled === undefined) company.systemEnabled = Boolean(company.systemUrl);
    if (company.driveUrl === undefined) company.driveUrl = '';
    if (company.driveEnabled === undefined) company.driveEnabled = Boolean(company.driveUrl);
    if (company.isClockedIn === undefined) company.isClockedIn = false;
    if (company.clockInTime === undefined) company.clockInTime = null;
    if (company.attendanceLogs === undefined) company.attendanceLogs = [];
  });

  return loadedCompanies;
};

export const loadFlowDeskData = async ({ storage, colorPalettes, defaultSoundSettings }) => {
  const savedLang = await storage.load(STORAGE_KEYS.language, 'ar');
  const savedTasksRaw = await storage.load(STORAGE_KEYS.tasks, []);
  const savedTasks = Array.isArray(savedTasksRaw) ? savedTasksRaw : [];
  const savedHealth = await storage.load(STORAGE_KEYS.healthSettings, { enabled: true, interval: 60, lastCheck: Date.now() });

  if (savedHealth) savedHealth.lastCheck = Date.now();

  const savedSound = await storage.load(STORAGE_KEYS.soundSettings, defaultSoundSettings);
  const savedGov = await storage.load(STORAGE_KEYS.governorate, 'Cairo');
  const savedUser = await storage.load(STORAGE_KEYS.userName, '');
  const savedPhoto = await storage.load(STORAGE_KEYS.userPhoto, null);
  const savedUserRole = await storage.load(STORAGE_KEYS.userRole, '');
  const savedCurrentUser = await storage.load(STORAGE_KEYS.currentUser, null);
  const savedWorkspaceUsers = await storage.load(STORAGE_KEYS.workspaceUsers, []);
  const savedAuthSession = await storage.load(STORAGE_KEYS.authSession, null);
  const savedWorkspaceNameRaw = await storage.load(STORAGE_KEYS.workspaceName, 'FlowDesk');
  const savedWorkspaceName = savedWorkspaceNameRaw === 'PixelFlow' ? 'FlowDesk' : savedWorkspaceNameRaw;
  const savedCurrentWorkspace = await storage.load(STORAGE_KEYS.currentWorkspace, null);
  const onboardingDone = await storage.load(STORAGE_KEYS.onboardingDone, false);
  const savedPrayerStatus = await storage.load(STORAGE_KEYS.prayerStatus, null);
  const savedDensity = await storage.load(STORAGE_KEYS.uiDensity, 'comfortable');
  const savedTheme = await storage.load(STORAGE_KEYS.theme, 'dark');
  const savedOperationMode = await storage.load(STORAGE_KEYS.operationMode, OPERATION_MODES.online);
  const savedSyncQueue = await storage.load(STORAGE_KEYS.syncQueue, []);
  const savedSyncConnection = await storage.load(STORAGE_KEYS.syncConnection, null);
  const savedSyncBackups = await storage.load(STORAGE_KEYS.syncBackups, []);

  const savedCompaniesRaw = (await storage.load(STORAGE_KEYS.companies, null)) || (await storage.load('pixelPlannerCompanies', null));
  const savedCompanies = savedCompaniesRaw || {};
  const companies = normalizeCompanies({
    companies: savedCompanies,
    tasks: savedTasks,
    lang: savedLang,
    colorPalettes
  });

  const today = new Date().toISOString().split('T')[0];
  const prayerStatus = savedPrayerStatus && savedPrayerStatus.date === today
    ? savedPrayerStatus.data
    : defaultPrayerStatus();
  const currentUser = createCurrentUser({
    ...(savedCurrentUser || {}),
    name: savedCurrentUser?.name ?? savedUser ?? '',
    photo: savedCurrentUser ? (savedCurrentUser.photo || null) : (savedPhoto || null),
    title: savedCurrentUser ? (savedCurrentUser.title || '') : (savedUserRole || '')
  });
  const operationMode = OPERATION_MODES.online;
  const currentWorkspace = createWorkspace({
    ...(savedCurrentWorkspace || {}),
    name: savedCurrentWorkspace?.name ?? savedWorkspaceName,
    ownerUserId: savedCurrentWorkspace?.ownerUserId ?? currentUser.id,
    mode: savedCurrentWorkspace?.mode ?? operationMode
  });
  const authSession = createAuthSession({
    ...(savedAuthSession || {}),
    isAuthenticated: savedAuthSession ? Boolean(savedAuthSession.isAuthenticated) : false,
    userId: savedAuthSession?.userId ?? currentUser.id,
    email: savedAuthSession?.email ?? currentUser.email
  });
  const workspaceUsers = Array.isArray(savedWorkspaceUsers) && savedWorkspaceUsers.length > 0
    ? savedWorkspaceUsers.map(user => createWorkspaceUser(user))
    : [createWorkspaceUser({
      id: currentUser.id,
      name: currentUser.name || savedUser || 'FlowDesk User',
      email: currentUser.email || authSession.email || '',
      title: currentUser.title || savedUserRole || '',
      accessRole: currentUser.accessRole,
      createdAt: currentWorkspace.createdAt
    })];

  const firstCompany = Object.values(companies)[0] || null;

  return {
    lang: savedLang,
    tasks: savedTasks,
    companies,
    healthSettings: savedHealth,
    soundSettings: { ...defaultSoundSettings, ...(savedSound || {}), adhanType: 'full' },
    governorate: savedGov,
    userName: savedUser,
    userPhoto: savedPhoto,
    userRole: savedUserRole,
    currentUser,
    workspaceUsers,
    authSession,
    workspaceName: savedWorkspaceName,
    currentWorkspace,
    prayerStatus,
    theme: savedTheme,
    uiDensity: savedDensity,
    operationMode,
    syncQueue: normalizeSyncQueue(savedSyncQueue),
    syncConnection: createSyncConnection({
      ...(savedSyncConnection || {}),
      enabled: true,
      autoSyncEnabled: true,
      autoSyncIntervalSeconds: 30
    }),
    syncBackups: Array.isArray(savedSyncBackups) ? savedSyncBackups : [],
    showOnboarding: !onboardingDone,
    onboardingData: {
      language: savedLang || 'ar',
      governorate: savedGov || 'Cairo',
      userName: savedUser || '',
      userRole: savedUserRole || '',
      workspaceName: savedWorkspaceName || 'FlowDesk',
      companyName: firstCompany?.name || '',
      clientsText: (firstCompany?.clients || []).join('\n'),
      managersText: (firstCompany?.managers || []).join('\n'),
      workStart: firstCompany?.workStart || '09:00',
      workEnd: firstCompany?.workEnd || '17:00',
      hourlyRate: firstCompany?.hourlyRate || 0
    }
  };
};
